import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { type AlertChannel, type AlertRule, api, ApiError, type NewAlertRule } from '@/api/client';
import { EnvBadge } from '@/pages/Sources';
import { baselineOf, byHowMuch, describeRule, type RuleSeed, sameTime, seedFrom, simulate, triggerLevel } from '@/lib/alerts';
import { ago, clockIn, compactCount, formatCount, shortStamp } from '@/lib/format';
import { useNow, useWidth } from '@/lib/hooks';
import { Link, navigate } from '@/lib/router';
import { useSettings } from '@/lib/settings';
import { usePageCommands, useShell } from '@/lib/shell';
import { isIncomplete } from '@/lib/suggest';
import { QueryHighlight } from '@/lib/text';

const WINDOWS = [1, 5, 10, 15, 30, 60, 180, 1440];
const KINDS: ReadonlyArray<[NewAlertRule['kind'], string, string]> = [
    ['threshold', 'Too many', 'more than N lines in the window'],
    ['absence', 'Too few', 'no line at all in the window'],
    ['relative', 'Unusual', 'more or fewer than yesterday or last week'],
    ['new_issue', 'New issue', 'an error never seen before'],
];
const STATE_LABEL: Readonly<Record<AlertRule['state'], string>> = { ok: 'OK', pending: 'Pending', firing: 'Firing' };
const EVENT_WORD: Readonly<Record<string, string>> = { fired: 'Fired', resolved: 'Resolved', new_issue: 'New issue' };

// ALERTS: rules over the lines (too many, too few, unusual, a new issue), checked every minute, confirmed before they fire and
// before they resolve, sent to Slack or any webhook. A rule's past shows before it is saved.
export function Alerts(): ReactNode {
    const { user, env, envs, say } = useShell();
    const queryClient = useQueryClient();
    const now = useNow(15_000);
    const alerts = useQuery({ queryKey: ['alerts'], queryFn: api.alerts, refetchInterval: 30_000 });
    // A link from EXPLORE or an issue opens a new rule filled in; the address then forgets it, so a refresh does not.
    const [seed] = useState(() => seedFrom(location.search));
    // A rule being edited, a new one, or a copy of one (for another environment, most of the time).
    const [editing, setEditing] = useState<AlertRule | 'new' | { copyOf: AlertRule } | null>(seed ? 'new' : null);
    useEffect(() => {
        if (seed) {
            navigate('/alerts', { replace: true });
        }
    }, [seed]);
    const [confirming, setConfirming] = useState<string | null>(null);
    const refresh = async (): Promise<void> => {
        await queryClient.invalidateQueries({ queryKey: ['alerts'] });
        await queryClient.invalidateQueries({ queryKey: ['firing'] });
    };
    const fail = (error: unknown): void => say(error instanceof ApiError ? error.message : 'The server did not answer');
    const update = useMutation({
        mutationFn: ({ rule, patch }: { rule: AlertRule; patch: Parameters<typeof api.updateRule>[1] }) => api.updateRule(rule.id, patch),
        onSuccess: refresh,
        onError: fail,
    });
    const remove = useMutation({
        mutationFn: (rule: AlertRule) => api.deleteRule(rule.id),
        onSuccess: async (_, rule) => {
            say(`Rule “${rule.name}” deleted`);
            setConfirming(null);
            await refresh();
        },
        onError: fail,
    });
    const rules = alerts.data?.rules ?? [];
    const channels = useMemo(() => alerts.data?.channels ?? [], [alerts.data]);
    const channelName = (id: string): string => channels.find((channel) => channel.id === id)?.name ?? 'removed';

    usePageCommands([{ icon: '+', label: 'New alert rule', run: () => setEditing('new') }]);

    return (
        <div className="page page-scroll">
            <div className="page-head">
                <div>
                    <h1>Alerts</h1>
                    <p>
                        Rules over the lines, checked every minute. A rule fires once its condition held for its confirmation checks and resolves once
                        it was clear as long: a blip wakes nobody.
                    </p>
                </div>
                <button className="act primary" onClick={() => setEditing(editing === 'new' ? null : 'new')}>
                    {editing === 'new' ? 'Close' : '+ New rule'}
                </button>
            </div>
            {editing && (
                <RuleForm
                    key={editing === 'new' ? 'new' : 'copyOf' in editing ? `copy:${editing.copyOf.id}` : editing.id}
                    rule={editing === 'new' || 'copyOf' in editing ? null : editing}
                    copyOf={editing !== 'new' && 'copyOf' in editing ? editing.copyOf : null}
                    seed={editing === 'new' ? seed : null}
                    env={env}
                    envs={envs}
                    channels={channels}
                    onDone={async (name) => {
                        setEditing(null);
                        say(`Rule “${name}” saved`);
                        await refresh();
                    }}
                    onCancel={() => setEditing(null)}
                    onError={fail}
                />
            )}
            <div className="panel">
                <table className="ptable alerts">
                    <thead>
                        <tr className="micro">
                            <th>State</th>
                            <th>Rule</th>
                            <th className="opt">Environment</th>
                            <th className="num">Now</th>
                            <th className="opt">Sends to</th>
                            <th className="opt">Checked</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {rules.map((rule) => {
                            const muted = rule.mutedUntil !== null && Date.parse(rule.mutedUntil) > now;
                            const key = (what: string): string => `${what}:${rule.id}`;

                            return (
                                <tr key={rule.id} className={rule.enabled ? '' : 'off'}>
                                    <td>
                                        <span className={`astate ${!rule.enabled ? 'off' : muted ? 'muted' : rule.state}`}>
                                            {!rule.enabled ? 'Off' : muted ? 'Muted' : STATE_LABEL[rule.state]}
                                        </span>
                                        {rule.state === 'firing' && rule.stateSince && (
                                            <div className="micro sub">for {ago(Date.parse(rule.stateSince), now)}</div>
                                        )}
                                    </td>
                                    <td>
                                        <div className="strong">
                                            {rule.name}{' '}
                                            <span className="phone-only">
                                                <EnvBadge env={rule.env} />
                                            </span>
                                        </div>
                                        <div className="rule-cond">
                                            {describeRule(rule)}
                                            {rule.q ? (
                                                <>
                                                    {' '}
                                                    matching <QueryHighlight q={rule.q} />
                                                </>
                                            ) : rule.kind === 'new_issue' ? (
                                                ''
                                            ) : (
                                                ' of any kind'
                                            )}
                                            {rule.confirmChecks > 1 && ` · for ${rule.confirmChecks} checks`}
                                        </div>
                                    </td>
                                    <td className="opt">
                                        <EnvBadge env={rule.env} />
                                    </td>
                                    <td className="num mono">
                                        {rule.kind === 'new_issue' ? (
                                            '–'
                                        ) : rule.lastValue === null ? (
                                            '…'
                                        ) : rule.kind === 'relative' ? (
                                            <div
                                                title={`${formatCount(rule.lastValue)} lines in the last ${rule.windowMinutes} min; ${rule.lastBaseline === null ? 'nothing to compare with' : formatCount(rule.lastBaseline)} ${sameTime(rule.compareDays)}`}
                                            >
                                                {formatCount(rule.lastValue)}
                                                <div className="micro sub">
                                                    {rule.lastBaseline === null ? 'no past yet' : `vs ${formatCount(rule.lastBaseline)}`}
                                                </div>
                                            </div>
                                        ) : (
                                            `${formatCount(rule.lastValue)}${rule.kind === 'threshold' ? ` / ${rule.threshold}` : ''}`
                                        )}
                                    </td>
                                    <td className="small opt">
                                        {rule.channelIds.length > 0 ? (
                                            rule.channelIds.map(channelName).join(' · ')
                                        ) : (
                                            <span className="warn">nowhere</span>
                                        )}
                                    </td>
                                    <td className="opt">{rule.lastEvaluatedAt ? `${ago(Date.parse(rule.lastEvaluatedAt), now)} ago` : 'not yet'}</td>
                                    <td className="row-actions">
                                        <button className="act" onClick={() => setEditing(rule)}>
                                            Edit
                                        </button>
                                        <button
                                            className="act"
                                            title="The same rule for another environment"
                                            onClick={() => setEditing({ copyOf: rule })}
                                        >
                                            Copy
                                        </button>
                                        <button className="act" onClick={() => update.mutate({ rule, patch: { mutedHours: muted ? 0 : 1 } })}>
                                            {muted ? 'Unmute' : 'Mute 1h'}
                                        </button>
                                        <button className="act" onClick={() => update.mutate({ rule, patch: { enabled: !rule.enabled } })}>
                                            {rule.enabled ? 'Turn off' : 'Turn on'}
                                        </button>
                                        <button
                                            className={`act ${confirming === key('delete') ? 'danger' : ''}`}
                                            onClick={() => (confirming === key('delete') ? remove.mutate(rule) : setConfirming(key('delete')))}
                                        >
                                            {confirming === key('delete') ? 'Delete?' : '×'}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        {rules.length === 0 && !alerts.isLoading && (
                            <tr>
                                <td colSpan={7} className="ptable-empty">
                                    No rule yet. Start with the API&apos;s errors, the cron going silent, or new issues in production.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="board-row two">
                <Events />
                <Channels channels={channels} admin={user.role === 'admin'} onChanged={refresh} say={say} />
            </div>
        </div>
    );
}

// A new rule starts from the defaults, or from one of the rules most teams want first, to adjust before saving.
const DEFAULTS: Omit<NewAlertRule, 'env' | 'channelIds'> = {
    name: '',
    kind: 'threshold',
    q: 'level:>=error',
    threshold: 20,
    direction: 'up',
    percent: 100,
    compareDays: 7,
    windowMinutes: 5,
    confirmChecks: 2,
    recoverChecks: 2,
};
const STARTERS: ReadonlyArray<Partial<NewAlertRule> & Pick<NewAlertRule, 'name' | 'kind'>> = [
    { name: 'API errors', kind: 'threshold', q: 'service:api level:>=error', threshold: 20, windowMinutes: 5 },
    { name: 'Errors doubled', kind: 'relative', q: 'level:>=error', direction: 'up', percent: 100, threshold: 20, windowMinutes: 15 },
    { name: 'Cron gone quiet', kind: 'absence', q: 'service:cron', windowMinutes: 15, confirmChecks: 1, recoverChecks: 1 },
    { name: 'API traffic dropped', kind: 'relative', q: 'service:api', direction: 'down', percent: 75, threshold: 50, windowMinutes: 15 },
    { name: 'New issues', kind: 'new_issue', q: '' },
];
const UP_BY = [50, 100, 200, 400, 900];
const DOWN_BY = [25, 50, 75, 90];

function RuleForm(props: {
    rule: AlertRule | null;
    // A copy starts as the rule, in another environment than its own when there is one.
    copyOf: AlertRule | null;
    seed: RuleSeed | null;
    env: string;
    envs: readonly string[];
    channels: readonly AlertChannel[];
    onDone: (name: string) => Promise<void>;
    onCancel: () => void;
    onError: (error: unknown) => void;
}): ReactNode {
    const { rule, copyOf } = props;
    const [values, setValues] = useState<NewAlertRule>(() => {
        const from = rule ?? copyOf;
        if (!from) {
            return { ...DEFAULTS, env: props.env, channelIds: props.channels.map((channel) => channel.id).slice(0, 1), ...props.seed };
        }
        const other = props.env !== from.env ? props.env : props.envs.find((item) => item !== from.env);

        return {
            name: from.name,
            env: copyOf && other ? other : from.env,
            kind: from.kind,
            q: from.q,
            threshold: from.threshold,
            direction: from.direction,
            percent: from.percent,
            compareDays: from.compareDays,
            windowMinutes: from.windowMinutes,
            confirmChecks: from.confirmChecks,
            recoverChecks: from.recoverChecks,
            channelIds: from.channelIds,
        };
    });
    const set = (patch: Partial<NewAlertRule>): void => setValues((current) => ({ ...current, ...patch }));
    const { kind, q } = values;
    const [debounced, setDebounced] = useState(q);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(q), 500);

        return () => clearTimeout(timer);
    }, [q]);
    const save = useMutation({
        mutationFn: () => {
            const body: NewAlertRule = { ...values, name: values.name.trim(), q: values.q.trim() };

            return rule ? api.updateRule(rule.id, body) : api.createRule(body);
        },
        onSuccess: () => props.onDone(values.name.trim()),
        onError: props.onError,
    });
    const incomplete = isIncomplete(q);

    return (
        <form
            className="panel form-card rule-form"
            onSubmit={(event) => {
                event.preventDefault();
                if (values.name.trim() && !incomplete) {
                    save.mutate();
                }
            }}
        >
            <div className="panel-head">
                <span className="title">{rule ? 'Edit the rule' : copyOf ? `A copy of “${copyOf.name}”` : 'New rule'}</span>
                <span className="note">{copyOf ? `from ${copyOf.env}, to the environment you choose` : 'checked every minute'}</span>
            </div>
            {!rule && !copyOf && (
                <div className="starters">
                    <span className="micro">Start from</span>
                    {STARTERS.map((starter) => (
                        <button
                            key={starter.name}
                            type="button"
                            className={`act ${values.name === starter.name ? 'on' : ''}`}
                            onClick={() => setValues({ ...DEFAULTS, env: values.env, channelIds: values.channelIds, ...starter })}
                        >
                            {starter.name}
                        </button>
                    ))}
                </div>
            )}
            <div className="form-grid">
                <label>
                    <span className="micro">Name</span>
                    <input
                        value={values.name}
                        onChange={(event) => set({ name: event.target.value })}
                        placeholder="API errors"
                        maxLength={100}
                        required
                        autoFocus={!rule}
                    />
                </label>
                <label>
                    <span className="micro">Environment</span>
                    <select value={values.env} onChange={(event) => set({ env: event.target.value })} disabled={Boolean(rule)}>
                        {[...new Set([...props.envs, values.env])].map((item) => (
                            <option key={item} value={item}>
                                {item}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
            <div className="kind-row">
                {KINDS.map(([key, label, hint]) => (
                    <button
                        key={key}
                        type="button"
                        className={`kind ${kind === key ? 'on' : ''}`}
                        onClick={() => set({ kind: key })}
                        disabled={Boolean(rule) && rule?.kind !== key}
                    >
                        <b>{label}</b>
                        <span>{hint}</span>
                    </button>
                ))}
            </div>
            <div className="form-grid">
                <label className="wide">
                    <span className="micro">Lines matching · the query language {kind === 'new_issue' ? '· optional' : ''}</span>
                    <div className={`qinput ${incomplete ? 'bad' : ''}`}>
                        <div className="qb-hl">
                            <QueryHighlight q={q} />
                        </div>
                        <input
                            value={q}
                            onChange={(event) => set({ q: event.target.value })}
                            spellCheck={false}
                            placeholder={kind === 'new_issue' ? 'every error' : 'every line'}
                        />
                    </div>
                </label>
                {kind === 'threshold' && (
                    <label>
                        <span className="micro">More than</span>
                        <input
                            type="number"
                            min={0}
                            value={values.threshold}
                            onChange={(event) => set({ threshold: Math.max(0, Number(event.target.value)) })}
                        />
                    </label>
                )}
                {kind === 'relative' && (
                    <>
                        <div className="field">
                            <span className="micro">When there are</span>
                            <span className="seg field-seg">
                                {(['up', 'down'] as const).map((direction) => (
                                    <button
                                        key={direction}
                                        type="button"
                                        className={values.direction === direction ? 'on' : ''}
                                        onClick={() => set({ direction, percent: direction === 'down' ? 50 : 100 })}
                                    >
                                        {direction === 'up' ? 'More' : 'Fewer'}
                                    </button>
                                ))}
                            </span>
                        </div>
                        <label>
                            <span className="micro">By</span>
                            <select value={values.percent} onChange={(event) => set({ percent: Number(event.target.value) })}>
                                {[...new Set([...(values.direction === 'up' ? UP_BY : DOWN_BY), values.percent])].map((percent) => (
                                    <option key={percent} value={percent}>
                                        {byHowMuch(values.direction, percent)}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span className="micro">Than the same time</span>
                            <select value={values.compareDays} onChange={(event) => set({ compareDays: Number(event.target.value) })}>
                                {[...new Set([1, 7, values.compareDays])].map((days) => (
                                    <option key={days} value={days}>
                                        {days === 1 ? 'yesterday' : days === 7 ? 'last week' : `${days} days ago`}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label
                            title={
                                values.direction === 'up'
                                    ? 'Fewer lines than this wake nobody, however many times the usual'
                                    : 'A usual smaller than this is too small to drop'
                            }
                        >
                            <span className="micro">{values.direction === 'up' ? 'With at least' : 'When it had at least'}</span>
                            <input
                                type="number"
                                min={1}
                                value={values.threshold}
                                onChange={(event) => set({ threshold: Math.max(1, Number(event.target.value)) })}
                            />
                        </label>
                    </>
                )}
                {kind !== 'new_issue' && (
                    <>
                        <label>
                            <span className="micro">In the last</span>
                            <select value={values.windowMinutes} onChange={(event) => set({ windowMinutes: Number(event.target.value) })}>
                                {WINDOWS.map((minutes) => (
                                    <option key={minutes} value={minutes}>
                                        {minutes < 60 ? `${minutes} min` : `${minutes / 60} h`}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span className="micro">Fires after</span>
                            <select value={values.confirmChecks} onChange={(event) => set({ confirmChecks: Number(event.target.value) })}>
                                {[1, 2, 3, 5, 10].map((checks) => (
                                    <option key={checks} value={checks}>
                                        {checks === 1 ? 'the first check' : `${checks} checks in a row`}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span className="micro">Resolves after</span>
                            <select value={values.recoverChecks} onChange={(event) => set({ recoverChecks: Number(event.target.value) })}>
                                {[1, 2, 3, 5, 10].map((checks) => (
                                    <option key={checks} value={checks}>
                                        {checks === 1 ? 'one clear check' : `${checks} clear checks`}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </>
                )}
            </div>
            {kind !== 'new_issue' && !isIncomplete(debounced) && <Preview q={debounced} rule={{ ...values, kind }} />}
            <div className="channel-pick">
                <span className="micro">Send to</span>
                {props.channels.map((channel) => (
                    <label key={channel.id} className="check">
                        <input
                            type="checkbox"
                            checked={values.channelIds.includes(channel.id)}
                            onChange={(event) =>
                                set({
                                    channelIds: event.target.checked
                                        ? [...values.channelIds, channel.id]
                                        : values.channelIds.filter((id) => id !== channel.id),
                                })
                            }
                        />
                        {channel.name} <span className="mono small">{channel.kind}</span>
                    </label>
                ))}
                {props.channels.length === 0 && (
                    <span className="warn">No channel yet: an admin adds Slack or a webhook below. The rule still records its events.</span>
                )}
            </div>
            <div className="dialog-actions">
                <button type="button" className="act" onClick={props.onCancel}>
                    Cancel
                </button>
                <button className="act primary" disabled={!values.name.trim() || incomplete || save.isPending}>
                    {save.isPending ? 'Saving…' : rule ? 'Save the rule' : copyOf ? `Create it in ${values.env}` : 'Create the rule'}
                </button>
            </div>
        </form>
    );
}

// The rule over the past: its count in each window, the threshold (for a relative rule, the same time yesterday or
// last week and where it fires), and when it would have fired.
function Preview(props: {
    q: string;
    rule: Omit<NewAlertRule, 'kind' | 'name' | 'channelIds'> & { kind: 'threshold' | 'absence' | 'relative' };
}): ReactNode {
    const { rule } = props;
    const { timeZone: zone } = useSettings();
    const [hours, setHours] = useState(24);
    const [ref, width] = useWidth<HTMLDivElement>();
    const relative = rule.kind === 'relative';
    const compare = relative ? rule.compareDays : undefined;
    const preview = useQuery({
        queryKey: ['alert-preview', rule.env, props.q, rule.windowMinutes, hours, compare ?? 0],
        queryFn: ({ signal }) => api.alertPreview({ env: rule.env, q: props.q, window: rule.windowMinutes, hours, compare }, signal),
        placeholderData: keepPreviousData,
        staleTime: 60_000,
    });
    const points = preview.data?.points ?? [];
    const firstAt = preview.data?.firstAt;
    const run = simulate(points, rule, firstAt);
    // A relative rule: each window's usual (its baseline) and the level where it fires, wherever there is a baseline.
    const usual = relative ? points.map((point) => baselineOf(point, rule.compareDays, firstAt)) : [];
    const levels = usual.map((base) => {
        if (base === null) {
            return null;
        }
        if (rule.direction === 'up') {
            return Math.max(rule.threshold, triggerLevel('up', base, rule.percent));
        }

        return base >= Math.max(1, rule.threshold) ? triggerLevel('down', base, rule.percent) : null;
    });
    const compared = usual.some((base) => base !== null);
    const height = 120;
    const max = Math.max(
        1,
        rule.kind === 'threshold' ? rule.threshold * 1.25 : 1,
        ...points.map((point) => point.count),
        ...usual.map((base) => base ?? 0),
        ...levels.map((level) => level ?? 0),
    );
    const bar = width / Math.max(1, points.length);
    const y = (value: number): number => height - 14 - (value / max) * (height - 22);
    const first = points[0]?.t ?? 0;
    const last = (points.at(-1)?.t ?? 0) + (preview.data?.stepMs ?? 0);
    const xOf = (t: number): number => ((t - first) / Math.max(1, last - first)) * width;
    // A value per window as steps, broken where there is none.
    const steps = (values: ReadonlyArray<number | null>): string =>
        values.reduce<string>(
            (path, value, index) =>
                value === null
                    ? path
                    : `${path}${index > 0 && values[index - 1] !== null ? 'L' : 'M'}${index * bar},${y(value)}H${(index + 1) * bar}`,
            '',
        );
    let verdict: string;
    if (preview.isFetching && points.length === 0) {
        verdict = 'Reading the past…';
    } else if (relative && !compared) {
        verdict = 'Nothing to compare with yet';
    } else if (run.episodes.length === 0) {
        verdict = 'It would not have fired';
    } else {
        verdict = `It would have fired ${run.episodes.length} ${run.episodes.length === 1 ? 'time' : 'times'}`;
    }

    return (
        <div className="preview">
            <div className="preview-head">
                <span className="micro">
                    The last{' '}
                    <span className="seg inline">
                        {[24, 72, 168].map((option) => (
                            <button key={option} type="button" className={hours === option ? 'on' : ''} onClick={() => setHours(option)}>
                                {option === 168 ? '7d' : `${option}h`}
                            </button>
                        ))}
                    </span>
                </span>
                <span className={`verdict ${relative && !compared ? '' : run.episodes.length > 0 ? 'bad' : 'good'}`}>{verdict}</span>
            </div>
            <div ref={ref} className="preview-chart">
                {width > 0 && points.length > 0 && (
                    <svg width={width} height={height}>
                        {run.episodes.map((episode) => (
                            <rect
                                key={episode.from}
                                className="episode"
                                x={xOf(episode.from)}
                                y={0}
                                width={Math.max(2, xOf(episode.to ?? last) - xOf(episode.from))}
                                height={height - 14}
                            />
                        ))}
                        {points.map((point, index) => (
                            <rect
                                key={point.t}
                                className={run.holding[index] ? 'over' : 'under'}
                                x={index * bar}
                                y={y(point.count)}
                                width={Math.max(1, bar - (bar > 3 ? 1 : 0))}
                                height={Math.max(point.count > 0 ? 1 : 0, height - 14 - y(point.count))}
                            />
                        ))}
                        {rule.kind === 'threshold' && <line className="threshold" x1={0} x2={width} y1={y(rule.threshold)} y2={y(rule.threshold)} />}
                        {relative && <path className="usual" d={steps(usual)} />}
                        {relative && <path className="threshold" d={steps(levels)} />}
                        <text className="axis" x={0} y={height - 2}>
                            {shortStamp(first, zone)}
                        </text>
                        <text className="axis" x={width - 4} y={height - 2} textAnchor="end">
                            {clockIn(new Date(last), zone).slice(0, 5)}
                        </text>
                        <text className="axis" x={width - 4} y={10} textAnchor="end">
                            {compactCount(Math.round(max))}
                        </text>
                    </svg>
                )}
            </div>
            {relative && (
                <div className="preview-legend micro">
                    <span>
                        <i className="lg usual" /> {sameTime(rule.compareDays)}
                    </span>
                    <span>
                        <i className="lg trig" /> where it fires
                    </span>
                    {!compared && points.length > 0 && (
                        <span className="note">
                            {firstAt
                                ? `The first line of ${rule.env} is from ${shortStamp(firstAt, zone)}: comparisons start ${shortStamp(firstAt + rule.compareDays * 86_400_000, zone)}`
                                : `No line in ${rule.env} yet`}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

function Events(): ReactNode {
    const { timeZone: zone } = useSettings();
    const alerts = useQuery({ queryKey: ['alerts'], queryFn: api.alerts });
    const events = alerts.data?.events ?? [];

    return (
        <div className="panel">
            <div className="panel-head">
                <span className="title">What happened</span>
                <span className="note">the latest 60 events</span>
            </div>
            <div className="events">
                {events.map((event) => (
                    <div key={event.id} className={`event ${event.kind}`}>
                        <span className="mono small">{shortStamp(Date.parse(event.at), zone)}</span>
                        <span className={`ekind ${event.kind}`}>{EVENT_WORD[event.kind] ?? event.kind}</span>
                        <span className="ename">
                            <EnvBadge env={event.env} /> {event.rule_name}
                            {event.kind === 'new_issue' && event.fingerprint ? (
                                <Link className="link" to={`/issues/${event.fingerprint}?env=${event.env}&range=24h`}>
                                    {' '}
                                    {event.title}
                                </Link>
                            ) : event.value !== null ? (
                                <span className="mono small"> · {formatCount(event.value)} lines</span>
                            ) : null}
                        </span>
                        <span className={`edelivery ${event.delivered ? 'ok' : 'no'}`} title={event.error ?? 'Sent'}>
                            {event.delivered ? 'Sent' : (event.error ?? 'Not sent')}
                        </span>
                    </div>
                ))}
                {events.length === 0 && <div className="bars-empty micro">Nothing yet</div>}
            </div>
        </div>
    );
}

function Channels(props: {
    channels: readonly AlertChannel[];
    admin: boolean;
    onChanged: () => Promise<void>;
    say: (message: string) => void;
}): ReactNode {
    const [adding, setAdding] = useState(false);
    const [name, setName] = useState('');
    const [kind, setKind] = useState<'slack' | 'webhook'>('slack');
    const [url, setUrl] = useState('');
    const [confirming, setConfirming] = useState<string | null>(null);
    const fail = (error: unknown): void => props.say(error instanceof ApiError ? error.message : 'The server did not answer');
    const add = useMutation({
        mutationFn: () => api.createChannel({ name: name.trim(), kind, url: url.trim() }),
        onSuccess: async () => {
            props.say(`Channel “${name.trim()}” added: send it a test`);
            setAdding(false);
            setName('');
            setUrl('');
            await props.onChanged();
        },
        onError: fail,
    });
    const test = useMutation({
        mutationFn: (channel: AlertChannel) => api.testChannel(channel.id),
        onSuccess: (_, channel) => props.say(`A test went to ${channel.name}`),
        onError: fail,
    });
    const remove = useMutation({
        mutationFn: (channel: AlertChannel) => api.deleteChannel(channel.id),
        onSuccess: async () => {
            setConfirming(null);
            await props.onChanged();
        },
        onError: fail,
    });

    return (
        <div className="panel">
            <div className="panel-head">
                <span className="title">Where alerts go</span>
                <span className="note no-phone">Slack or a webhook</span>
                <span className="spacer" />
                {props.admin && (
                    <button className="act" onClick={() => setAdding(!adding)}>
                        {adding ? 'Close' : '+ Channel'}
                    </button>
                )}
            </div>
            {adding && (
                <form
                    className="channel-form"
                    onSubmit={(event) => {
                        event.preventDefault();
                        add.mutate();
                    }}
                >
                    <input placeholder="#alerts-prod" value={name} onChange={(event) => setName(event.target.value)} required maxLength={80} />
                    <select value={kind} onChange={(event) => setKind(event.target.value as 'slack' | 'webhook')}>
                        <option value="slack">Slack incoming webhook</option>
                        <option value="webhook">Any webhook (JSON POST)</option>
                    </select>
                    <input
                        placeholder="https://hooks.slack.com/services/…"
                        value={url}
                        onChange={(event) => setUrl(event.target.value)}
                        required
                        type="url"
                    />
                    <button className="act primary" disabled={add.isPending}>
                        Add
                    </button>
                    {kind === 'slack' && (
                        <p className="channel-hint">
                            In Slack:{' '}
                            <a className="link" href="https://api.slack.com/apps" target="_blank" rel="noreferrer">
                                api.slack.com/apps
                            </a>{' '}
                            → an app of yours → Incoming Webhooks → Add New Webhook → the channel; paste its address here. The address is a secret:
                            once saved, only its end shows.
                        </p>
                    )}
                </form>
            )}
            <div className="versions">
                {props.channels.map((channel) => (
                    <div key={channel.id} className="version">
                        <span>
                            <b className="strong">{channel.name}</b>{' '}
                            <span className="mono small">
                                {channel.kind} · {channel.address}
                            </span>
                        </span>
                        <span className="row-actions">
                            <button className="act" onClick={() => test.mutate(channel)}>
                                Test
                            </button>
                            {props.admin && (
                                <button
                                    className={`act ${confirming === channel.id ? 'danger' : ''}`}
                                    onClick={() => (confirming === channel.id ? remove.mutate(channel) : setConfirming(channel.id))}
                                >
                                    {confirming === channel.id ? 'Remove?' : '×'}
                                </button>
                            )}
                        </span>
                    </div>
                ))}
                {props.channels.length === 0 && (
                    <div className="micro">
                        {props.admin ? 'No channel yet: add a Slack incoming webhook.' : 'No channel yet: an admin adds one.'}
                    </div>
                )}
            </div>
        </div>
    );
}
